/**
 * store/scratch.ts — whether the track on «Сейчас играет» is ready to be scratched.
 *
 * Task 18. `spec.md` «Пластинка»: «СКРЕТЧ» is lit exactly when a finger on the record would get
 * the scratch — the track ready, and the sound of the scratch there and not refused. In a slice of
 * its own: it changes a few times a track, and nothing that draws the list or the player is
 * subscribed to it. Written by `engine/scratch.ts` and by nothing else.
 *
 * Task 57: each deck has its own (`byDeck`); the turntable reads the deck it shows. No flag of «the
 * active deck's»: it would be the active deck's at its last write, wrong after the decks change.
 *
 * Task 62: each deck's loop too (`loops`) — the voice plays it (`engine/scratch.ts`, «Loops»): the
 * pult lights LOOP by it, and the turntable's status row says «ПЕТЛЯ» or «ДРОБЬ».
 */

import { create } from 'zustand';

import type { DeckSide } from '../lib/pult-layout';

/** Task 62: a deck's loop, as the pult and the turntable show it. */
export interface DeckLoop {
  /** Where it starts and ends, in seconds of the track. */
  readonly startS: number;
  readonly endS: number;
  /** Its length in beats of the track, as the pult names it. */
  readonly beats: number;
  /** A roll: the track goes on under it, and comes back where it would be. */
  readonly roll: boolean;
}

export interface ScratchState {
  readonly byDeck: Readonly<Record<DeckSide, boolean>>;
  /** Task 62: each deck's loop, while its voice plays one. */
  readonly loops: Readonly<Record<DeckSide, DeckLoop | undefined>>;
}

export const useScratch = create<ScratchState>(() => ({
  byDeck: { A: false, B: false },
  loops: { A: undefined, B: undefined },
}));

/** Deck `side` is ready to be scratched, or not. */
export function markReady(ready: boolean, side: DeckSide): void {
  useScratch.setState({ byDeck: { ...useScratch.getState().byDeck, [side]: ready } });
}

/** Task 62: deck `side` loops `loop` now, or nothing with `undefined`. */
export function markLoop(loop: DeckLoop | undefined, side: DeckSide): void {
  if (useScratch.getState().loops[side] === loop) return;
  useScratch.setState({ loops: { ...useScratch.getState().loops, [side]: loop } });
}
